/**
 * Where the programme sits on the approval journey, and what it can do next.
 *
 * @module components/governance/GovernanceJourney
 */

import React from "react";

import {
  Badge,
  Button,
  Divider,
  Heading,
  KeyValue,
  Stepper,
  type StepState,
  Text,
} from "@/components/ui";
import type { EditorProgrammeMetadata, ProgrammePlatformClient } from "@/platform";
import { NO_INTERNAL_REVIEWER } from "@/server/reviewer-requirement";

import { APPROVAL_STATIONS, approvalStationIndex } from "./governance-stations";
import {
  GovernanceRequirements,
  type GovernanceRequirementsProps,
  openRequirementCount,
} from "./GovernanceRequirements";
import { GovernanceWorkflowActions } from "./GovernanceWorkflowActions";
import type { useProgrammeGovernance } from "./useProgrammeGovernance";

export interface GovernanceJourneyProps {
  model: ReturnType<typeof useProgrammeGovernance>;
  client: ProgrammePlatformClient;
  programme: EditorProgrammeMetadata;
  hasUnsavedChanges: boolean;
  hrefFor: GovernanceRequirementsProps["hrefFor"];
  /** Drawn inside the stage's details, after the requirements: its approval record. */
  children?: React.ReactNode;
  /** A user's name, from their id. */
  nameFor: (userId: string) => string;
}

export function GovernanceJourney({
  model,
  client,
  programme,
  hasUnsavedChanges,
  hrefFor,
  children = undefined,
  nameFor,
}: GovernanceJourneyProps): React.ReactElement | null {
  const {
    currentStationIndex,
    expanded,
    governance,
    navigationItems,
    reviewRounds,
    setExpanded,
    stage,
  } = model;
  if (!governance) {
    return null;
  }
  const archived = programme.workflowState === "Archived";
  const endedAt = programme.archivedFrom ? approvalStationIndex(programme.archivedFrom) : -1;
  // Routed on assignment: no reviewer on the open round, or none to submit to.
  const latestRound = reviewRounds.at(-1);
  const internalReviewSkipped =
    currentStationIndex <= 1
      ? programme.workflowState !== "Awaiting Internal Review" &&
        programme.workflowState !== "Internal Review" &&
        governance.activeReviewerUserIds.length === 0
      : latestRound !== undefined && latestRound.round.requiredReviewerUserIds.length === 0;
  return (
    <div className="mt-4">
      <Divider className="mb-4" />
      <div data-testid="approval-journey">
        {archived ? (
          <Text tone="muted" className="mb-2" data-testid="approval-journey-archived">
            {programme.archivedFrom ? `Archived from ${programme.archivedFrom}.` : "Archived."} This
            programme left the approval journey before it was exported.
          </Text>
        ) : null}
        {/* An archive older than its recorded state has no station to end on. */}
        {!archived || endedAt >= 0 ? (
          <Stepper
            aria-label="Programme approval progress"
            steps={APPROVAL_STATIONS.map((station, index) => {
              let state: StepState = "pending";
              if (archived) {
                if (index < endedAt) {
                  state = "done";
                } else if (index === endedAt) {
                  state = "ended";
                }
              } else if (station.key === "internal-review" && internalReviewSkipped) {
                state = "skipped";
              } else if (index < currentStationIndex) {
                state = "done";
              } else if (index === currentStationIndex) {
                state = "current";
              }
              return {
                key: station.key,
                label: station.label,
                state,
                ...(state === "skipped" ? { hint: NO_INTERNAL_REVIEWER } : {}),
                "data-testid": `approval-station-${station.key}`,
              };
            })}
          />
        ) : null}
      </div>

      <GovernanceWorkflowActions
        model={model}
        client={client}
        programme={programme}
        hasUnsavedChanges={hasUnsavedChanges}
        hrefFor={hrefFor}
        nameFor={nameFor}
      />

      <Button
        variant="outline"
        size="sm"
        className="mt-3 w-full justify-between"
        iconEnd={expanded ? "caret-up" : "caret-down"}
        aria-expanded={expanded}
        aria-controls="governance-panel-content"
        data-testid="governance-panel-toggle"
        onClick={() => setExpanded((current) => !current)}
      >
        <span className="me-auto">
          {expanded ? "Hide readiness details and guidance" : "Show readiness details and guidance"}
        </span>
        <Badge size="sm" variant="outline">
          {openRequirementCount(governance, programme.workflowState)} open
        </Badge>
      </Button>

      <div
        id="governance-panel-content"
        className="mt-4 space-y-5"
        data-testid="governance-panel-content"
        hidden={!expanded}
      >
        <section data-testid="governance-stage-guidance">
          <div className="flex flex-wrap items-center gap-2">
            <Heading level={3}>What happens next</Heading>
            <Badge tone="primary" size="sm" data-testid="governance-stage-label">
              {stage.label}
            </Badge>
          </div>
          <KeyValue
            columns={3}
            className="mt-3"
            entries={[
              {
                label: "Who acts",
                value: stage.responsible,
                "data-testid": "governance-stage-owner",
              },
              {
                label: "How to move forward",
                value: stage.nextStep,
                "data-testid": "governance-stage-next-step",
              },
              { label: "What changes", value: stage.result },
            ]}
          />
        </section>

        <GovernanceRequirements
          governance={governance}
          navigationItems={navigationItems}
          hrefFor={hrefFor}
          workflowState={programme.workflowState}
        />
        {children}
      </div>
    </div>
  );
}
